import { checkTimer, getSession, isLoggedIn } from "@/utils/auth";
import { BASIC, CLASSIC, ENTERPRISE, ESSENTIAL, PRO, SECONDARY, SPOT, SPOT_CON } from "@/utils/clientTypes";
import SegmentAnalytics from "@/utils/segmentAnalytics";
import Vue from "vue";
import VueRouter, { isNavigationFailure } from "vue-router";

Vue.use(VueRouter);

// vue-router 3.1+ rechaza la promesa de push/replace ante fallas de navegación
// benignas: NavigationDuplicated (navegar a la ruta actual), redirect desde un
// guard, navegación cancelada. En llamados fire-and-forget (sin callbacks) eso
// termina como unhandledrejection y ensucia Sentry. Tragamos sólo esas fallas
// de navegación — cualquier otro error se re-lanza. Patrón canónico para esta
// versión de vue-router (los callers que pasan callbacks conservan el flujo
// original). Ref: vue-router#2881.
["push", "replace"].forEach((method) => {
    const original = VueRouter.prototype[method];
    VueRouter.prototype[method] = function (location, onResolve, onReject) {
        if (onResolve || onReject) {
            return original.call(this, location, onResolve, onReject);
        }
        return original.call(this, location).catch((err) => {
            if (isNavigationFailure(err)) return err;
            return Promise.reject(err);
        });
    };
});

// Client types con acceso al dashboard. Antes cada uno tenía su propia ruta
// `/dashboard` duplicada (6 records con el mismo path) y navegar por path
// siempre resolvía la primera (CLASSIC). Ahora una sola ruta con authRequired
// combinado; el guard usa esta lista para el dispatch post-login. SPOT/SPOT-CON
// no tienen dashboard → caen a Error404 (comportamiento preservado).
const DASHBOARD_CLIENT_TYPES = [CLASSIC, BASIC, PRO, ENTERPRISE, ESSENTIAL, SECONDARY];

const routes = [
    {
        path: "/",
        name: "Login",
        // Sólo "/login": vue-router exige leading slash en rutas no anidadas.
        // El alias relativo "login" disparaba el warning y era redundante.
        alias: "/login",
        component: () => import("@/views/login/Login.vue"),
    },
    {
        path: "/login-as-other-user",
        name: "Login as other user",
        component: () => import("@/views/login/Login.vue"),
        props: () => {
            return { isAnalist: true }
        }
    },
    {
        path: "/create-another-user/:uuid",
        name: "Create another user for an existing company",
        component: () => import("@/views/login/RegisterAnother.vue"),
    },
    {
        path: "/registerchita",
        name: "RegisterChita",
        component: () => import("@/views/login/RegisterChita.vue"),
    },
    {
        path: "/registerbasicfree",
        name: "RegisterBasic",
        component: () => import("@/views/login/RegisterFast.vue"),
        props: () => {
            return { type: BASIC }
        }
    },
    {
        path: "/registeressential",
        name: "RegisterEssential",
        component: () => import("@/views/login/RegisterFast.vue"),
        props: () => {
            return { type: ESSENTIAL }
        }
    },
    {
        path: "/registerenterprise",
        name: "RegisterEnterprise",
        component: () => import("@/views/login/RegisterFast.vue"),
        props: () => {
            return { type: ENTERPRISE }
        }
    },
    {
        path: "/register",
        name: "Register",
        component: () => import("@/views/login/RegisterOnboarding.vue"),
    },
    {
        path: "/recoveryconfirm",
        name: "RecoveryConfirm",
        component: () => import("@/views/login/RecoveryConfirm.vue"),
    },
    {
        path: "/recoverypassword",
        name: "RecoveryPassword",
        component: () => import("@/views/login/RecoveryPassword.vue"),
    },
    {
        path: "/changepassword/:token",
        name: "ChangePassword",
        component: () => import("@/views/login/ChangePassword.vue"),
    },
    {
        path: "/confirm",
        name: "Confirm",
        component: () => import("@/views/login/Confirm.vue"),
        props: (route) => Object.assign({}, route.query, route.params),
    },
    {
        path: "/thankyou",
        name: "Thankyou",
        component: () => import("@/views/login/Thankyou.vue"),
    },
    {
        path: "/go-to-plan",
        name: "Choose plan",
        // Despachador puro: el guard (to.name === "Choose plan") siempre
        // redirige según el client_type, así que este component casi nunca se
        // monta. Está para que la ruta no quede sin nada que renderizar (B2).
        component: () => import("@/views/RedirectDispatcher.vue"),
        meta: { requiresAuth: true },
    },
    {
        path: "/panel-analista",
        name: "panel-analista",
        component: () => import("@/views/PanelAnalista.vue"),
        meta: { requiresAuth: true, authRequired: [BASIC, CLASSIC, ENTERPRISE, PRO, SECONDARY, SPOT, SPOT_CON, ESSENTIAL] },
    },
    {
        path: "/info-company",
        name: "companyData",
        component: () => import("@/views/CompanyInfo.vue"),
        meta: { requiresAuth: true, authRequired: [BASIC, CLASSIC, ENTERPRISE, PRO, SECONDARY, SPOT, SPOT_CON, ESSENTIAL] },
        props: true,
    },
    {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: DASHBOARD_CLIENT_TYPES },
    },
    {
        path: "/plan-basic",
        redirect: '/dashboard',
    },
    {
        path: "/plan-essential",
        redirect: '/dashboard',
    },
    {
        path: "/plan-enterprise",
        redirect: '/dashboard',
    },
    {
        path: "/plan-pro",
        redirect: '/dashboard',
    },
    {
        path: "/filldata",
        name: "FillData",
        component: () => import("@/views/FillData.vue"),
        meta: { requiresAuth: true, authRequired: [ESSENTIAL, BASIC] },
    },
    {
        path: "/404",
        name: "Error404",
        component: () => import("@/views/errors/Error404.vue"),
    },
    {
        path: "*",
        name: "ErrorPage",
        component: () => import("@/views/errors/ErrorPage.vue"),
    },
];

const router = new VueRouter({
    routes,
});

// Auto-recuperación ante ChunkLoadError. Tras un deploy, una pestaña que quedó
// abierta con una build vieja pide sus chunks lazy con hashes que ya no
// existen en S3; CloudFront responde con el index.html de fallback y webpack
// lo convierte en ChunkLoadError (el "Unexpected token '<'" en consola), con
// el usuario clavado en una pantalla rota. Recargamos la página para traer el
// shell nuevo (index.html se sirve con no-store) — una sola vez por pestaña
// (flag en sessionStorage) para no entrar en loop si el chunk sigue fallando
// por otra causa (red caída, build rota).
export const CHUNK_RELOAD_FLAG = "chunkLoadErrorReloaded";

export function isChunkLoadError(error) {
    if (!error) return false;
    // name cubre los chunks JS (webpack); el regex, los chunks CSS de
    // mini-css-extract-plugin, que rechazan con un Error genérico.
    return error.name === "ChunkLoadError" || /loading( css)? chunk .+ failed/i.test(error.message || "");
}

export function handleChunkLoadError(error, { storage = window.sessionStorage, reload = () => window.location.reload() } = {}) {
    if (!isChunkLoadError(error)) return;
    // Si sessionStorage está bloqueado (modo privado restrictivo) asumimos que
    // ya recargamos: mejor dejar el error visible que arriesgar un reload-loop.
    let alreadyReloaded = true;
    try {
        alreadyReloaded = Boolean(storage.getItem(CHUNK_RELOAD_FLAG));
        if (!alreadyReloaded) storage.setItem(CHUNK_RELOAD_FLAG, "1");
    } catch (err) {
        // storage inaccesible
    }
    if (!alreadyReloaded) reload();
}

export function clearChunkReloadFlag({ storage = window.sessionStorage } = {}) {
    try {
        storage.removeItem(CHUNK_RELOAD_FLAG);
    } catch (err) {
        // storage inaccesible
    }
}

router.onError(handleChunkLoadError);
// Una navegación exitosa prueba que la build actual resuelve sus chunks:
// rearmamos el guard de un-solo-reload para el próximo deploy.
router.afterEach(() => clearChunkReloadFlag());

// Guard exportado (no inline) para poder testearlo en aislamiento con
// (to, from, next) mockeados, sin tener que conducir una navegación real.
export async function authGuard(to, from, next) {
    checkTimer();
    // A2: si la verificación de sesión rechaza (red caída, etc.) no dejamos que
    // el throw aborte el guard sin llamar next() (navegación colgada / pantalla
    // en blanco). Lo tratamos como no-logueado: la rama de requiresAuth redirige
    // a Login y las rutas públicas pasan.
    let isLogged;
    try {
        isLogged = await isLoggedIn();
    } catch (err) {
        isLogged = false;
    }
    new SegmentAnalytics().page(to.name);
    if (isLogged) {
        const session = getSession();
        if (session) {
            // A3: blindar `to.name` — si es undefined (ruta sin name),
            // `to.name.startsWith(...)` tiraba TypeError dentro del guard async
            // y abortaba la navegación (pantalla trabada).
            if ((to.name && to.name.startsWith("Register")) || to.name === "Login") {
                next({ name: "Choose plan" });
            } else if (to.name === "Choose plan" || to.path === "/") {
                // Redirigir desde un guard se hace con next({...}), NO con
                // router.push: push deja la navegación actual pendiente y, si
                // apunta a la ruta donde ya estamos, rechaza con
                // NavigationDuplicated (unhandledrejection).
                if (session.user.user_type == "ANALISTA" && session.user.is_staff == true) {
                    next({ name: "panel-analista" });
                } else {
                    // Una sola ruta de dashboard: si el client_type tiene acceso
                    // va ahí; si no (p.ej. SPOT/SPOT-CON), a Error404.
                    next(DASHBOARD_CLIENT_TYPES.includes(session.user.client_type)
                        ? { name: "dashboard" }
                        : { name: "Error404" });
                }
            } else if (to.matched.some((record) => record.meta.requiresAuth)) {
                if (
                    session.user.user_type != "ANALISTA" &&
                    // A3: `authRequired` puede faltar en una ruta requiresAuth;
                    // sin el `|| []` el `.indexOf` tiraría y abortaría el guard.
                    (to.meta.authRequired || []).indexOf(session.user.client_type) < 0
                ) {
                    next({ name: "Choose plan" });
                } else {
                    next();
                }
            } else {
                next();
            }
        } else {
            // A1: estado inconsistente (isLogged pero sin session) — antes no se
            // llamaba next() y la navegación quedaba colgada (pantalla en
            // blanco). Un guard debe llamar next() siempre: mandamos a Login.
            next({ name: "Login" });
        }
    } else if (to.matched.some((record) => record.meta.requiresAuth)) {
        // D: redirigir por `name` (sin `path` redundante) y preservar el
        // destino real en `query.redirect = to.fullPath` (antes era siempre
        // "/"). Queda listo para que Login.vue, cuando lo consuma, devuelva al
        // usuario a donde quería ir tras autenticarse.
        next({ name: "Login", query: { redirect: to.fullPath } });
    } else {
        next();
    }
}

router.beforeEach(authGuard);

export default router;
